/* eslint-disable complexity */
import React, {
    useReducer, useEffect, useCallback, useRef, useMemo, useLayoutEffect
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import localeUtils from 'utils/LanguageLocale';
import mediaUtils from 'utils/Media';
import { colors, radii } from 'style/config';
import {
    Grid, Link, Text, Box
} from 'components/ui';
import Dropdown from 'components/Dropdown/Dropdown';
import Chevron from 'components/Chevron/Chevron';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import skuUtils from 'utils/Sku';
import SwatchDescription from 'components/ProductPage/Swatches/SwatchDescription';
import SwatchGroup from 'components/ProductPage/Swatches/SwatchGroup';
import SwatchTypeSelector from 'components/ProductPage/Swatches/SwatchTypeSelector';
import SwatchImage from 'components/ProductPage/Swatches/SwatchImage';
import swatchUtils from 'utils/Swatch';
import ShadeFinderText from 'components/ProductPage/ShadeFinderText/ShadeFinderText';
import actions from 'Actions';
import { COLOR_SWATCH_DISPLAY } from 'constants/TestTarget';
import filterUtils from 'utils/Filters';
import { DebouncedResize } from 'constants/events';
import Helpers from 'utils/Helpers';
import { showHorizontalSizeSelectorSelector } from 'viewModel/selectors/testTarget/showHorizontalSizeSelectorSelector';
import { showCollapseShadeSelectorsSelector } from 'viewModel/selectors/testTarget/showCollapseShadeSelectorsSelector';
import SwatchesEN from 'components/ProductPage/Swatches/locales/Swatches_en_US';
import SwatchesFR from 'components/ProductPage/Swatches/locales/Swatches_fr_CA';

const { getSwatchType, SWATCH_GROUP_VIEWS } = swatchUtils;
const { Media } = mediaUtils;
const getText = localeUtils.getLocalisedDataFromResource(SwatchesEN, SwatchesFR);
const { deferTaskExecution } = Helpers;

const initialState = {
    selectedSwatchView: SWATCH_GROUP_VIEWS.GRID,
    showSwatchSelectorType: false,
    showDropdown: false,
    isDropOpen: false,
    isSmallView: null
};

const isDefaultViewEligible = ({ showCollapseShadeSelectors, product, isSmallView, isItemSubModal }) =>
    showCollapseShadeSelectors && product.variationType === skuUtils.skuVariationType.COLOR && isSmallView && !isItemSubModal;

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_VIEW':
            return { ...state, selectedSwatchView: action.value };
        case 'SET_SWATCH_SELECTOR_TYPE':
            return { ...state, showSwatchSelectorType: action.value };
        case 'SET_DROPDOWN':
            return {
                ...state,
                showDropdown: action.value,
                selectedSwatchView: action.swatchView || state.selectedSwatchView,
                isSmallView: action.isSmallView !== undefined ? action.isSmallView : state.isSmallView
            };
        case 'SET_DROP_OPEN':
            return { ...state, isDropOpen: action.value };
        default:
            return state;
    }
};

const SwatchesDisplay = props => {
    const {
        product,
        isSkuReady,
        loveIcon,
        colorIQMatch,
        hideSizeAndDescription,
        isItemSubModal,
        hideSwatchSelectorType,
        showColorMatch,
        wizardResult,
        wizardMatchText,
        hideListBorder,
        noMaxHeight,
        swatchGroups: swatchGroupsProp,
        swatchFilters,
        customCss
    } = props;

    const [state, localDispatch] = useReducer(reducer, {
        ...initialState,
        selectedSwatchView: props.selectedSwatchView || SWATCH_GROUP_VIEWS.GRID
    });

    const dispatch = useDispatch();
    const showHorizontalSizeSelector = useSelector(showHorizontalSizeSelectorSelector);
    const showCollapseShadeSelectors = useSelector(showCollapseShadeSelectorsSelector);
    const colorSwatchDisplayExperience = useSelector(state_ => {
        const result = state_.testTarget?.offers?.colorSwatchDisplay?.result;

        return result?.experience || COLOR_SWATCH_DISPLAY.CURRENT;
    });

    const selectedItemRef = useRef(null);
    const dropdownRef = useRef(null);
    const dropdownSelectedItemRef = useRef(null);
    const scrollRef = useRef(null);
    const needToScrollRef = useRef(false);
    const userClickedRef = useRef(false);
    // True once the shopper manually picks a view, disabling auto-select on resize
    const userSelectedViewRef = useRef(false);
    const isFirstRenderRef = useRef(true);
    const prevSkuRef = useRef(product?.currentSku);
    const prevProductRef = useRef(product);
    const prevProductIdRef = useRef(product?.productDetails?.productId);

    const { currentSku } = product;
    const isFragrance = skuUtils.isFragrance(product, currentSku);
    const selectedSku = product.hoveredSku || currentSku;

    // Stabilize groupRef instances across renders so child SwatchGroups receive the
    // same ref object as long as the groupName is unchanged. React.createRef() inside
    // useMemo would produce a brand-new ref on every recompute (whenever
    // swatchGroupsProp or swatchFilters changes), causing unnecessary ref reassignment.
    const groupRefsMapRef = useRef({});
    const swatchGroupsData = useMemo(
        () =>
            swatchGroupsProp.map((skus, index) => {
                const groupName = swatchFilters[index];
                const key = groupName ?? `__idx_${index}`;

                if (!groupRefsMapRef.current[key]) {
                    groupRefsMapRef.current[key] = React.createRef();
                }

                return {
                    groupName,
                    skus,
                    groupRef: groupRefsMapRef.current[key]
                };
            }),
        [swatchGroupsProp, swatchFilters]
    );

    // Preserve the class's hydration guard: original showSmallView() delegated to
    // mediaUtils.isMobileView() which returns false on server and before hydration
    // finishes. Reusing it here avoids SSR/hydration mismatches with the dropdown vs
    // grid render path when matchMedia would otherwise report a mobile viewport
    // before the client has hydrated.
    const showSmallView = useCallback(() => mediaUtils.isMobileView(), []);

    const handleResize = useCallback(() => {
        let selectedSwatchView = state.selectedSwatchView;
        const isDropdownTest = colorSwatchDisplayExperience === COLOR_SWATCH_DISPLAY.SHOW_HYBRID && !isFragrance;
        const isSmallView = showSmallView();

        if (isSmallView && isDropdownTest && product.skuSelectorType === skuUtils.skuSwatchType.IMAGE) {
            if (!state.showDropdown) {
                localDispatch({
                    type: 'SET_DROPDOWN',
                    value: true,
                    swatchView: props.selectedSwatchView || SWATCH_GROUP_VIEWS.GRID,
                    isSmallView
                });
            }
        } else {
            const canDefault = isDefaultViewEligible({
                showCollapseShadeSelectors,
                product,
                isSmallView,
                isItemSubModal
            });

            if (!userSelectedViewRef.current) {
                selectedSwatchView = canDefault ? SWATCH_GROUP_VIEWS.DEFAULT : props.selectedSwatchView || SWATCH_GROUP_VIEWS.GRID;
            } else if (selectedSwatchView === SWATCH_GROUP_VIEWS.DEFAULT && !canDefault) {
                selectedSwatchView = SWATCH_GROUP_VIEWS.GRID;
            }

            const hasChanged = state.showDropdown !== false || state.selectedSwatchView !== selectedSwatchView || state.isSmallView !== isSmallView;

            if (hasChanged) {
                localDispatch({ type: 'SET_DROPDOWN', value: false, swatchView: selectedSwatchView, isSmallView });
            }
        }
    }, [
        state.selectedSwatchView,
        state.showDropdown,
        colorSwatchDisplayExperience,
        isFragrance,
        product,
        props.selectedSwatchView,
        showSmallView,
        showCollapseShadeSelectors,
        isItemSubModal
    ]);

    useEffect(() => {
        window.addEventListener(DebouncedResize, handleResize);
        handleResize();

        return () => window.removeEventListener(DebouncedResize, handleResize);
    }, [handleResize]);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;

            return;
        }

        handleResize();
    }, [colorSwatchDisplayExperience]);

    useEffect(() => {
        if (product !== prevProductRef.current) {
            prevProductRef.current = product;
            localDispatch({
                type: 'SET_SWATCH_SELECTOR_TYPE',
                value: product.skuSelectorType === skuUtils.skuSwatchType.IMAGE && !!swatchGroupsProp.find(skus => skus.length > 1)
            });
        }

        if (product?.productDetails?.productId !== prevProductIdRef.current) {
            prevProductIdRef.current = product?.productDetails?.productId;
            userSelectedViewRef.current = false;
        }
    }, [product, swatchGroupsProp]);

    useLayoutEffect(() => {
        if (product?.currentSku !== prevSkuRef.current) {
            prevSkuRef.current = product?.currentSku;

            if (userClickedRef.current) {
                userClickedRef.current = false;
            } else {
                needToScrollRef.current = true;
            }
        }

        const isListScroll = state.selectedSwatchView === SWATCH_GROUP_VIEWS.LIST;
        const isDefaultScroll = state.selectedSwatchView === SWATCH_GROUP_VIEWS.DEFAULT;

        if (needToScrollRef.current && selectedItemRef.current && (isListScroll || isDefaultScroll)) {
            deferTaskExecution(() => {
                const selectedItem = selectedItemRef.current;
                // List view scrolls one shared vertical container; Default view scrolls the horizontal row that owns the swatch
                const container = isListScroll
                    ? scrollRef.current
                    : swatchGroupsData.find(({ groupRef }) => groupRef.current?.contains(selectedItem))?.groupRef.current;

                if (!selectedItem || !container) {
                    return;
                }

                const itemRect = selectedItem.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();

                needToScrollRef.current = false;

                if (isListScroll) {
                    container.scroll(0, itemRect.top - containerRect.top + container.scrollTop);
                } else {
                    // center the selected swatch in the row; preserve scrollTop so the row never jumps vertically
                    const centeredLeft = itemRect.left - containerRect.left + container.scrollLeft - (container.clientWidth / 2 - itemRect.width / 2);
                    container.scroll(centeredLeft, container.scrollTop);
                }
            });
        }
    }, [product?.currentSku, swatchGroupsProp, swatchGroupsData, state.selectedSwatchView]);

    const showShadeFinder = useCallback(
        currentProduct => {
            dispatch(actions.showWizard(true, currentProduct));
            const world = digitalData.page.attributes.world;
            pdpAnalytics.trackPageLoad({
                pageName: `product:${anaConsts.PAGE_DETAIL.SHADE_FINDER_LANDING}:${world ? world.toLowerCase() : 'n/a'}:*`,
                pageType: 'product',
                pageDetail: anaConsts.PAGE_DETAIL.SHADE_FINDER_LANDING,
                internalCampaign: `product:shade finder:${currentProduct.productDetails.productId.toLowerCase()}`,
                world: world && world.toLowerCase()
            });
        },
        [dispatch]
    );

    const handleSwatchTypeSelector = useCallback((e, typeSelected) => {
        e.preventDefault();
        userSelectedViewRef.current = true;
        deferTaskExecution(() => {
            localDispatch({ type: 'SET_VIEW', value: SWATCH_GROUP_VIEWS[typeSelected] });
            needToScrollRef.current = true;
            deferTaskExecution(() => {
                pdpAnalytics.trackSwatchSelect(typeSelected);
            });
        });
    }, []);

    const onSelectDropdownItemClick = useCallback(() => {
        userClickedRef.current = true;

        if (dropdownRef.current) {
            deferTaskExecution(() => dropdownRef.current.triggerDropdown(null, false));
        }
    }, []);

    const onSelectRegularItemClick = useCallback(() => {
        userClickedRef.current = true;
    }, []);

    const onTriggerDropdown = useCallback((e, isDropdownOpen) => {
        deferTaskExecution(() => {
            localDispatch({ type: 'SET_DROP_OPEN', value: isDropdownOpen });
            const selectedItem = dropdownSelectedItemRef?.current;

            if (selectedItem) {
                const menu = scrollRef.current;
                menu.scrollTop = selectedItem.offsetTop - (menu.offsetHeight / 2 - selectedItem?.offsetHeight / 2);
            }
        });
    }, []);

    const getAllSkusWithGroups = useCallback(() => {
        const allSkusWithGroups = swatchGroupsData.flatMap(({ groupName, skus }) => skus.map(sku => ({ sku, groupName })));

        return { skus: allSkusWithGroups.map(item => item.sku), skusWithGroupNames: allSkusWithGroups };
    }, [swatchGroupsData]);

    const renderGroupsViews = useCallback(
        (swatchView, isDropdown) => {
            if (showHorizontalSizeSelector && !isDropdown && swatchUtils.isHorizontalSizeVariationType(product.variationType)) {
                const { skus, skusWithGroupNames } = getAllSkusWithGroups();

                return (
                    <SwatchGroup
                        key='horizontal-size-selector'
                        index={0}
                        isSkuReady={isSkuReady}
                        isFragrance={isFragrance}
                        swatchView={swatchView}
                        groupName={null}
                        scrollRef={swatchGroupsData[0]?.groupRef}
                        selectedItemRef={selectedItemRef}
                        skus={skus}
                        skusWithGroupNames={skusWithGroupNames}
                        currentSku={currentSku}
                        currentProduct={product}
                        showColorMatch={showColorMatch}
                        wizardResult={wizardResult}
                        wizardMatchText={wizardMatchText}
                        isDropdown={isDropdown}
                        swatchItemCallback={onSelectRegularItemClick}
                        colorIQMatch={colorIQMatch}
                        showHorizontalSizeSelector={showHorizontalSizeSelector}
                        {...props}
                    />
                );
            }

            return swatchGroupsData.map(({ groupName, groupRef, skus }, index) => (
                <SwatchGroup
                    key={`swatch-group-${index}`}
                    index={index}
                    isSkuReady={isSkuReady}
                    isFragrance={isFragrance}
                    swatchView={swatchView}
                    groupName={groupName}
                    scrollRef={groupRef}
                    selectedItemRef={isDropdown ? dropdownSelectedItemRef : selectedItemRef}
                    skus={skus}
                    currentSku={currentSku}
                    currentProduct={product}
                    showColorMatch={showColorMatch}
                    wizardResult={wizardResult}
                    wizardMatchText={wizardMatchText}
                    isDropdown={isDropdown}
                    swatchItemCallback={isDropdown ? onSelectDropdownItemClick : onSelectRegularItemClick}
                    colorIQMatch={colorIQMatch}
                    showHorizontalSizeSelector={showHorizontalSizeSelector}
                    {...props}
                />
            ));
        },
        [props, swatchGroupsData, isFragrance, getAllSkusWithGroups, onSelectDropdownItemClick, onSelectRegularItemClick, showHorizontalSizeSelector]
    );

    const renderSizeText = useCallback((curSku, selSku) => {
        const shouldPreserveSpace = curSku.size && curSku.size !== curSku.variationValue;

        if (!shouldPreserveSpace) {
            return null;
        }

        const hasSizeData = selSku.size && selSku.size !== selSku.variationValue;
        const currentSizeLabel = `${getText('size')} ${curSku.size}`;

        return (
            <span css={sizeTextStyles.wrapper}>
                <Text
                    fontSize='sm'
                    aria-hidden='true'
                    children={currentSizeLabel}
                    css={sizeTextStyles.hidden}
                />
                <Text
                    fontSize='sm'
                    data-at={Sephora.debug.dataAt('sku_size_label')}
                    children={hasSizeData ? `${getText('size')} ${selSku.size}` : ' '}
                    css={[sizeTextStyles.overlay, !hasSizeData && sizeTextStyles.hidden]}
                />
            </span>
        );
    }, []);

    const {
        selectedSwatchView, showSwatchSelectorType, showDropdown, isDropOpen, isSmallView
    } = state;
    const { isReverseLookupEnabled } = product;
    const sizeText = renderSizeText(currentSku, selectedSku);

    const showDefaultView = isDefaultViewEligible({
        showCollapseShadeSelectors,
        product,
        isSmallView,
        isItemSubModal
    });
    const swatchViewOptions = showDefaultView
        ? [SWATCH_GROUP_VIEWS.DEFAULT, SWATCH_GROUP_VIEWS.GRID, SWATCH_GROUP_VIEWS.LIST]
        : [SWATCH_GROUP_VIEWS.GRID, SWATCH_GROUP_VIEWS.LIST];

    const renderSwatchGroups = () => {
        if (swatchGroupsData.length <= 0) {
            return null;
        }

        const isListView = selectedSwatchView === SWATCH_GROUP_VIEWS.LIST;
        const dataAt = Sephora.debug.dataAt('list_section');
        const dropdownGroups = showDropdown ? renderGroupsViews(SWATCH_GROUP_VIEWS.LIST, true) : null;

        if (isListView) {
            return (
                <Box
                    ref={scrollRef}
                    overflowY='auto'
                    data-at={dataAt}
                    borderY={hideListBorder ? 0 : 1}
                    border={[null, hideListBorder ? null : 1]}
                    borderColor={['lightGray', 'midGray']}
                    marginX={['-container', 0]}
                    borderRadius={[null, 2]}
                    maxHeight={noMaxHeight ? 'none' : [190, 210]}
                    css={{ paddingBottom: 2, marginBottom: 2, ...customCss?.swatchGroup }}
                >
                    {renderGroupsViews(selectedSwatchView, false)}
                </Box>
            );
        }

        return (
            <>
                {showDropdown && (
                    <Dropdown
                        id='swatches'
                        ref={dropdownRef}
                        useClick={true}
                        closeOnClick={false}
                        onTrigger={onTriggerDropdown}
                    >
                        <Dropdown.Trigger width='100%'>
                            <Grid
                                is='span'
                                gap={2}
                                columns='auto 1fr auto'
                                alignItems='center'
                                paddingY={1}
                                paddingX={3}
                                border={1}
                                borderColor='midGray'
                                borderTopLeftRadius={2}
                                borderTopRightRadius={2}
                                borderBottomLeftRadius={isDropOpen || 2}
                                borderBottomRightRadius={isDropOpen || 2}
                                borderBottomColor={isDropOpen && 'lightGray'}
                                minHeight={52}
                                css={{ '.no-touch &:hover': { backgroundColor: colors.nearWhite } }}
                            >
                                <SwatchImage
                                    src={currentSku.smallImage}
                                    type={getSwatchType(product.swatchType)}
                                    isOutOfStock={currentSku.isOutOfStock}
                                    isMatch={wizardResult?.skuId === currentSku.skuId || showColorMatch?.skuId === currentSku.skuId}
                                />
                                <span>
                                    <b>{getText('selected')}:</b> {filterUtils.getDescription(currentSku)}
                                </span>
                                <Chevron direction={isDropOpen ? 'up' : 'down'} />
                            </Grid>
                        </Dropdown.Trigger>
                        <Dropdown.Menu
                            ref={scrollRef}
                            hasTransition={false}
                            boxShadow={null}
                            border={1}
                            borderColor='midGray'
                            borderTopWidth={0}
                            maxHeight={240}
                            paddingY={2}
                            borderRadius={`0 0 ${radii[2]}px ${radii[2]}px`}
                        >
                            {dropdownGroups}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                {renderGroupsViews(selectedSwatchView, false)}
            </>
        );
    };

    return (
        <>
            {!hideSizeAndDescription && (
                <Grid
                    columns='1fr auto'
                    alignItems={[null, null, 'flex-end']}
                >
                    <Grid gap={1}>
                        {showDropdown ? sizeText : <SwatchDescription />}
                        {isSkuReady && (
                            <ShadeFinderText
                                colorIQMatch={colorIQMatch}
                                product={product}
                            />
                        )}
                    </Grid>
                    {loveIcon && <Media lessThan='md'>{loveIcon}</Media>}
                </Grid>
            )}
            {!hideSwatchSelectorType && !isFragrance && !showDropdown && !hideSizeAndDescription && (showSwatchSelectorType || sizeText) && (
                <Grid
                    columns='1fr auto'
                    alignItems='center'
                >
                    <div>{sizeText}</div>
                    {showSwatchSelectorType && (
                        <SwatchTypeSelector
                            onSelect={handleSwatchTypeSelector}
                            activeType={selectedSwatchView}
                            options={swatchViewOptions}
                        />
                    )}
                </Grid>
            )}
            {renderSwatchGroups()}
            {isReverseLookupEnabled && !isItemSubModal && (
                <div>
                    <Link
                        padding={2}
                        margin={-2}
                        color='blue'
                        onClick={() => showShadeFinder(product)}
                        data-at={Sephora.debug.dataAt('shade_finder_button')}
                        children={getText('shadeFinder')}
                    />
                </div>
            )}
        </>
    );
};

const sizeTextStyles = {
    wrapper: { position: 'relative', display: 'block' },
    hidden: { visibility: 'hidden' },
    overlay: { position: 'absolute', top: 0, left: 0 }
};

export { SwatchesDisplay };

export default wrapFunctionalComponent(SwatchesDisplay, 'SwatchesDisplay');
